/**
 * Каркас страницы: контейнер, заголовок, секция-карточка.
 *
 * До этого каждая страница выбирала ширину и отступы на глаз — сканер жил в
 * max-w-7xl с px-3, алерты в max-w-6xl, ключи в max-w-5xl с p-6, профиль в
 * max-w-5xl с py-8. При переходе между вкладками контент прыгал по горизонтали
 * и «дышал» по-разному, из-за чего интерфейс выглядел как несколько разных
 * приложений. Ширина теперь выбирается ОСМЫСЛЕННО (сколько колонок реально
 * нужно), а поля и вертикальный ритм — общие для всех.
 */
import React from 'react';
import { cn } from './cn';

/** Ширина колонки контента. Выбирается по плотности данных, а не по вкусу:
 *  wide — широкие таблицы (сканер, маршруты, PnL), где важна каждая колонка;
 *  default — формы и списки в две колонки (алерты, ключи, профиль);
 *  narrow — линейное чтение (чат, чужой профиль), где длинная строка утомляет. */
type PageWidth = 'wide' | 'default' | 'narrow';

const WIDTH: Record<PageWidth, string> = {
  wide:    'max-w-7xl',
  default: 'max-w-6xl',
  narrow:  'max-w-3xl',
};

export const PageContainer: React.FC<
  React.HTMLAttributes<HTMLDivElement> & { width?: PageWidth }
> = ({ className, width = 'default', children, ...props }) => (
  <div
    className={cn(
      'mx-auto w-full px-4 py-6 sm:px-6 lg:px-8',
      // space-y вместо отступов на каждой секции: иначе стоит кому-то забыть
      // mb — и в вертикальном ритме появляется дырка на одной странице из шести.
      'space-y-5',
      WIDTH[width],
      className,
    )}
    {...props}
  >
    {children}
  </div>
);

/**
 * Шапка страницы: имя, пояснение и действия справа.
 *
 * Размер h1 один на все вкладки — раньше «Управление API» было text-3xl,
 * «Профиль» text-lg, остальные text-xl, и вкладки ощущались разного «веса».
 * Подпись прячем на телефоне: там она отжимала кнопки на третью строку.
 */
export const PageHeader: React.FC<{
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}> = ({ title, description, icon, actions, className }) => (
  <div className={cn('flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between', className)}>
    <div className="min-w-0">
      {/* 30px, как у эталона. 24px терялись рядом с шапкой приложения —
          страница начиналась без внятной точки входа для глаза. */}
      <h1 className="flex items-center gap-2.5 text-2xl font-semibold tracking-tight sm:text-3xl">
        {icon && <span className="text-primary [&_svg]:size-6">{icon}</span>}
        <span className="truncate">{title}</span>
      </h1>
      {description && (
        <p className="mt-1.5 hidden text-sm text-muted-foreground sm:block">{description}</p>
      )}
    </div>
    {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

/**
 * Секция-карточка. Заголовок отделён линией, а не воздухом: в плотном
 * интерфейсе с десятком блоков только линия внятно говорит, где кончается
 * одна тема и начинается другая.
 *
 * `flush` — для таблиц: у них свои поля в ячейках, и внешний padding
 * добавил бы вторую рамку вокруг данных.
 */
export const Section: React.FC<{
  title?: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  flush?: boolean;
  className?: string;
  bodyClassName?: string;
  children?: React.ReactNode;
}> = ({ title, description, icon, actions, flush, className, bodyClassName, children }) => (
  <section className={cn('overflow-hidden rounded-xl border border-border bg-card shadow-sm', className)}>
    {(title || actions) && (
      // Заголовок секции 16px и поля 20px — было 14px и 16px, из-за чего
      // карточки выглядели плотнее и мельче эталона на каждом экране.
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-5 py-3.5">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            {icon && <span className="text-primary [&_svg]:size-4">{icon}</span>}
            {title}
          </h2>
          {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    )}
    {children != null && (
      <div className={cn(flush ? '' : 'p-5', bodyClassName)}>{children}</div>
    )}
  </section>
);

/** Подпись над полем. Один размер на все формы — иначе в одной карточке
 *  соседствуют лейблы 10px и 11px, и сетка выглядит кривой. */
export const FieldLabel: React.FC<React.LabelHTMLAttributes<HTMLLabelElement>> = ({ className, ...props }) => (
  <label className={cn('block text-[13px] font-medium text-muted-foreground', className)} {...props} />
);

/** Поле формы: подпись + контрол. */
export const Field: React.FC<{ label: React.ReactNode; hint?: React.ReactNode; className?: string; children: React.ReactNode }> =
  ({ label, hint, className, children }) => (
  <div className={cn('min-w-0 space-y-1', className)}>
    <FieldLabel>{label}</FieldLabel>
    {children}
    {hint && <p className="text-[13px] text-muted-foreground">{hint}</p>}
  </div>
);
